import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Navigate, useLocation, useSearchParams } from "react-router-dom";
import IndexApp from "../../Components/IndexApp";
import { SETTINGS_NAV_INDEX } from "../../Components/AppBar/AppBarConfigs";
import PartnerAdviceBanner from "../../Components/shared/PartnerAdviceBanner";
import { SettingsStyleContext } from "../../Components/Settings";
import { SettingsContextType, SettingsProvider } from "../../Contexts/SettingsContext";
import { AppDispatch, store } from "../../Redux/store";
import { getSponsorship } from "../../Redux/Actions/SponsorshipActions";
import { ENV_VERIFIED_TRAINER, getActiveBrandConfig, isBrandActive } from "../../config/environment.config";
import { DV2 } from "../DeclarationV2/theme";
import { vocab } from "../DeclarationV2/vocabulary";
import { useDv2Theme } from "../DeclarationV2/layout/Ui";
import ProfilStep from "../Settings/StepSettings/ProfilStep";
import FacturationStep from "../Settings/StepSettings/FacturationStep";
import SponsorshipStep from "../Settings/StepSettings/SponsorshipStep";
import BusinessStep from "../Settings/StepSettings/BusinessStep";
import DescriptionsV2 from "./DescriptionsV2";

export type SettingsTab = "profil" | "facturation" | "descriptions" | "parrainage" | "entreprise";
export const SETTINGS_TAB_PARAM = "onglet";
/** Anciens numéros d'étape (`location.state.step`), encore envoyés par l'accueil, la déclaration et les notifications. */
const TAB_FROM_LEGACY_STEP: Record<number, SettingsTab> = { 1: "profil", 2: "facturation", 3: "parrainage", 4: "entreprise", 5: "descriptions" };
const TEACHR_LEGACY_DOCUMENTS_STEP = 6;
const NAV_BAR = { index: SETTINGS_NAV_INDEX };

const isSettingsTab = (value: string | null): value is SettingsTab =>
    !!value && ["profil", "facturation", "descriptions", "parrainage", "entreprise"].includes(value);

/**
 * Paramètres du prof repensés (maquettes 8a à 8e du 1er novembre 2026) : un
 * menu de gauche qui dit ce que contient chaque onglet, les mêmes rubriques
 * et les mêmes étapes qu'aujourd'hui habillées en cartes par groupe de
 * champs (contexte `SettingsStyleContext`), et l'onglet Descriptions réécrit.
 * L'ancienne page (`TeachrSettings`, `Settings/Index`) reste dans le code.
 */
const TeachrSettingsV2 = () => {
    const teachr = useSelector((state: any) => state?.user?.currentUser?.teachr);
    const sponsorship = useSelector((state: any) => state?.sponsorship?.sponsorship || []);
    const urssafTdae = useSelector((state: any) => state?.user?.urssafTdae || []);
    const dispatch = useDispatch<AppDispatch>();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const { primary, fontFamily, text } = useDv2Theme();

    // ── Pagination du parrainage, comme dans TeachrSettings ─────────────────
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(0);
    const [limit, setLimit] = useState(8);
    const [totalItems, setTotalItems] = useState(8);
    const paginate = useCallback((pageNumber: number) => setCurrentPage(pageNumber), []);
    const dataDispatch = useMemo(() => ({
        teachr, page: currentPage, limit, setCurrentPage, setLimit, setTotalItems, paginate, isData: true, global: { setLoading, teachr },
    }), [currentPage, limit, paginate, teachr]);
    const dataChild = useMemo(() => ({
        current: { setCurrentPage }, loading: { loading, setLoading }, limit: { limit }, items: { totalItems }, pagination: { paginate },
    }), [loading, limit, totalItems, paginate]);
    useEffect(() => {
        if (teachr?.id) store.dispatch(getSponsorship({ teachr, page: currentPage, limit }));
    }, [teachr, currentPage, limit]);

    const dataSponsor = useMemo(() => sponsorship ?? [], [sponsorship]);
    const contextValue: SettingsContextType = useMemo(() => ({ location, teachr, dataSponsor, dispatch }), [dataSponsor, dispatch, location, teachr]);

    // ── Onglets ─────────────────────────────────────────────────────────────
    const isVt = isBrandActive(ENV_VERIFIED_TRAINER);
    const isValidTeachr = !!(teachr?.isAE && teachr?.isValidated);
    const tabs: Array<{ id: SettingsTab; title: string; subtitle: string; hidden?: boolean }> = [
        { id: "profil", title: "Profil", subtitle: "Identité et coordonnées" },
        { id: "facturation", title: "Facturation", subtitle: "IBAN et TVA" },
        { id: "descriptions", title: "Descriptions", subtitle: `Déroulement de vos ${vocab.courses()}` },
        { id: "parrainage", title: isVt ? "Équipe" : "Parrainage", subtitle: isVt ? "Coachs et commissions" : "Filleuls et gains", hidden: !isValidTeachr && !isVt },
        { id: "entreprise", title: "Entreprise", subtitle: "SIRET et agrément", hidden: !isValidTeachr },
    ];
    const visibleTabs = tabs.filter((t) => !t.hidden);
    const legacyStep: number | undefined = (location.state as any)?.step;
    const legacyTab = typeof legacyStep === "number" ? TAB_FROM_LEGACY_STEP[legacyStep] : undefined;
    const paramTab = searchParams.get(SETTINGS_TAB_PARAM);
    const requested: SettingsTab = isSettingsTab(paramTab) ? paramTab : (legacyTab ?? "profil");
    const tab: SettingsTab = visibleTabs.some((t) => t.id === requested) ? requested : "profil";

    useEffect(() => {
        if (!isSettingsTab(paramTab) && legacyTab) {
            const next = new URLSearchParams(searchParams);
            next.set(SETTINGS_TAB_PARAM, legacyTab);
            setSearchParams(next, { replace: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const selectTab = (next: SettingsTab) => {
        const params = new URLSearchParams(searchParams);
        params.set(SETTINGS_TAB_PARAM, next);
        setSearchParams(params, { replace: true });
    };

    // Ancienne rubrique « Mes documents » (étape 6) : redirigée vers la page dédiée, state compris.
    if (legacyStep === TEACHR_LEGACY_DOCUMENTS_STEP) {
        return <Navigate to={`/${getActiveBrandConfig()}/tableau-de-bord/documents`} state={location.state} replace />;
    }

    const content = () => {
        switch (tab) {
            case "facturation": return <FacturationStep teachr={teachr} />;
            case "descriptions": return <DescriptionsV2 teachr={teachr} />;
            case "parrainage": return <SponsorshipStep teachr={teachr} sponsorship={dataSponsor} dataChild={dataChild} dataDispatch={dataDispatch} />;
            case "entreprise": return <BusinessStep teachr={teachr} urssafTdae={urssafTdae} />;
            default: return <ProfilStep />;
        }
    };

    return (
        <IndexApp
            navBar={NAV_BAR}
            teachr={teachr}
            component={
                <SettingsProvider value={contextValue}>
                    <SettingsStyleContext.Provider value="v2">
                        <main className="px-4" style={{ background: DV2.pageBg, minHeight: "calc(100vh - 64px)", fontFamily, color: text }}>
                            <div className="mx-auto w-full /max-w-6xl px-4 sm:px-6 py-4 lg:py-8 flex flex-col gap-5">
                                <header>
                                    <h1 style={{ fontSize: 26, fontWeight: 800, margin: 0, lineHeight: 1.2 }}>Paramètres</h1>
                                    <div style={{ fontSize: 14, color: DV2.muted, marginTop: 4 }}>Votre profil, votre facturation et vos préférences</div>
                                </header>
                                <PartnerAdviceBanner variant="compact" />
                                <div className="grid grid-cols-1 lg:grid-cols-[240px_minmax(0,1fr)] gap-6 items-start">
                                    <nav aria-label="Rubriques des paramètres" style={{ background: DV2.surface, border: `1px solid ${DV2.border}`, borderRadius: DV2.radius, padding: 10 }} className="lg:sticky lg:top-24">
                                        <div className="flex lg:flex-col gap-1 overflow-x-auto" role="tablist">
                                            {visibleTabs.map((t) => {
                                                const selected = t.id === tab;
                                                return (
                                                    <button
                                                        key={t.id}
                                                        type="button"
                                                        role="tab"
                                                        aria-selected={selected}
                                                        onClick={() => selectTab(t.id)}
                                                        className="text-left flex-shrink-0"
                                                        style={{ fontFamily, padding: "12px 14px", borderRadius: 12, border: "none", background: selected ? DV2.tint : "transparent", cursor: "pointer", minWidth: 150 }}
                                                    >
                                                        <div style={{ fontWeight: 800, fontSize: 14, color: selected ? primary : text }}>{t.title}</div>
                                                        <div style={{ fontSize: 12, color: DV2.muted }}>{t.subtitle}</div>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </nav>
                                    <div className="min-w-0" key={tab}>
                                        {teachr?.id ? content() : null}
                                    </div>
                                </div>
                            </div>
                        </main>
                    </SettingsStyleContext.Provider>
                </SettingsProvider>
            }
        />
    );
};

export default TeachrSettingsV2;
